import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const { getCartCount } = useCart();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setShowUserMenu(false);
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md shadow-lg border-b-4 border-orange-500">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-300">
              <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z" />
              </svg>
            </div>
            <span className="text-2xl font-display font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              AfriArt
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-700 hover:text-orange-600 font-medium transition-colors duration-200">
              Home
            </Link>
            <Link to="/marketplace" className="text-gray-700 hover:text-orange-600 font-medium transition-colors duration-200">
              Marketplace
            </Link>
            <Link to="/artists" className="text-gray-700 hover:text-orange-600 font-medium transition-colors duration-200">
              Artists
            </Link>
            {isAuthenticated && (
              <Link to="/messages" className="text-gray-700 hover:text-orange-600 font-medium transition-colors duration-200">
                Messages
              </Link>
            )}
          </div>

          {/* Right Side Icons */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Cart */}
            <Link to="/cart" className="relative p-2 hover:bg-orange-50 rounded-full transition-colors duration-200">
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {getCartCount() > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {getCartCount()}
                </span>
              )}
            </Link>

            {/* User Menu */}
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 p-2 hover:bg-orange-50 rounded-full transition-colors duration-200"
                >
                  <div className="w-9 h-9 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center text-white font-bold">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-african border border-orange-100 py-2">
                    <Link
                      to="/profile"
                      onClick={() => setShowUserMenu(false)}
                      className="block px-4 py-2 text-gray-700 hover:bg-orange-50 transition-colors duration-200"
                    >
                      Profile
                    </Link>
                    {(user?.role === 'artist' || user?.role === 'admin') && (
                      <Link
                        to="/artist-dashboard"
                        onClick={() => setShowUserMenu(false)}
                        className="block px-4 py-2 text-gray-700 hover:bg-orange-50 transition-colors duration-200"
                      >
                        My Artworks
                      </Link>
                    )}
                    <Link
                      to="/orders"
                      onClick={() => setShowUserMenu(false)}
                      className="block px-4 py-2 text-gray-700 hover:bg-orange-50 transition-colors duration-200"
                    >
                      My Orders
                    </Link>
                    {user?.role === 'admin' && (
                      <Link
                        to="/admin"
                        onClick={() => setShowUserMenu(false)}
                        className="block px-4 py-2 text-purple-700 hover:bg-purple-50 transition-colors duration-200 font-semibold"
                      >
                        👑 Admin Panel
                      </Link>
                    )}
                    <hr className="my-1 border-orange-100" />
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 transition-colors duration-200"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link to="/login" className="btn-outline text-sm">
                  Login
                </Link>
                <Link to="/register" className="btn-primary text-sm">
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-orange-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-orange-100">
          <div className="px-4 py-3 space-y-3">
            <Link to="/" onClick={() => setIsOpen(false)} className="block py-2 text-gray-700 hover:text-orange-600">
              Home
            </Link>
            <Link to="/marketplace" onClick={() => setIsOpen(false)} className="block py-2 text-gray-700 hover:text-orange-600">
              Marketplace
            </Link>
            <Link to="/artists" onClick={() => setIsOpen(false)} className="block py-2 text-gray-700 hover:text-orange-600">
              Artists
            </Link>
            {isAuthenticated && (
              <Link to="/messages" onClick={() => setIsOpen(false)} className="block py-2 text-gray-700 hover:text-orange-600">
                Messages
              </Link>
            )}
            <Link to="/cart" onClick={() => setIsOpen(false)} className="block py-2 text-gray-700 hover:text-orange-600">
              Cart ({getCartCount()})
            </Link>
            {isAuthenticated ? (
              <>
                <Link to="/profile" onClick={() => setIsOpen(false)} className="block py-2 text-gray-700 hover:text-orange-600">
                  Profile
                </Link>
                {(user?.role === 'artist' || user?.role === 'admin') && (
                  <Link to="/artist-dashboard" onClick={() => setIsOpen(false)} className="block py-2 text-gray-700 hover:text-orange-600">
                    My Artworks
                  </Link>
                )}
                <Link to="/orders" onClick={() => setIsOpen(false)} className="block py-2 text-gray-700 hover:text-orange-600">
                  My Orders
                </Link>
                {user?.role === 'admin' && (
                  <Link to="/admin" onClick={() => setIsOpen(false)} className="block py-2 text-purple-700 hover:text-purple-600 font-semibold">
                    👑 Admin Panel
                  </Link>
                )}
                <button onClick={handleLogout} className="block w-full text-left py-2 text-red-600">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setIsOpen(false)} className="block py-2 text-gray-700 hover:text-orange-600">
                  Login
                </Link>
                <Link to="/register" onClick={() => setIsOpen(false)} className="block py-2 text-orange-600 font-semibold">
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;